/**
 * The confirm dialog for any write that changes how users sign in (doc 28d
 * §10.2): a routing switch, a domain removal or attestation, or disabling /
 * deleting a provider of a tenant that routes by email domain.
 *
 * It shows the API's preview (GET /identity-providers/routing-preview, or the
 * preview a 409 CONFIRM_REQUIRED carried): users moving to the password or to
 * a provider after registration, the brokered-only users who would be
 * stranded (named, up to 20), the devices whose fail mode changes, and — on
 * its own (VSNP-1707) — the users whose answer BEFORE sign-in changes (what
 * the new email-first apps get from the email: a verified domain finds its
 * provider in both routing modes). When anyone moves, Confirm stays
 * disabled until the admin ticks the explicit acknowledgement. Cancel (or
 * Escape, or the backdrop) closes it and sends nothing.
 *
 * The API enforces the same rule (confirm=true), so this dialog is the UX,
 * not the guard.
 */
import { useState, type ReactNode } from 'react';
import { AlertTriangle, ArrowRight, KeyRound, Lock, Users } from 'lucide-react';
import Modal from '../common/Modal';
import type { RoutingPreview } from '../../services/access.service';

const MODE_LABEL: Record<RoutingPreview['mode']['before'], string> = {
  local: 'SecureLink passwords only',
  all: 'everyone through the provider',
  domains: 'routing by email domain',
};

interface RoutingPreviewDialogProps {
  isOpen: boolean;
  title: string;
  /** What the write does, in a sentence. */
  description: ReactNode;
  preview: RoutingPreview | null;
  /** The preview is being fetched. */
  loading?: boolean;
  /** The preview could not be fetched (the write is still possible; the API decides). */
  error?: string | null;
  confirmText: string;
  /** The write is in flight. */
  busy?: boolean;
  /** Fields of the caller's own (a reason), shown above the counts. */
  children?: ReactNode;
  /** The caller's own fields are not valid yet. */
  confirmDisabled?: boolean;
  /** The provider the write is about, for the before-sign-in lines. */
  providerName?: string;
  variant?: 'danger' | 'primary';
  onConfirm: () => void;
  onCancel: () => void;
}

function plural(n: number, word: string, pluralWord = `${word}s`): string {
  return `${n} ${n === 1 ? word : pluralWord}`;
}

/** Some user's pre-sign-in answer changes (an API before VSNP-1707's follow-up sends no discovery counts). */
function discoveryMoving(p: RoutingPreview): boolean {
  const d = p.discoveryChanges;
  return !!d && (p.discoveryChanged ?? d.toPassword + d.toProvider + d.providerChanged > 0);
}

/**
 * The acknowledgement: what changes after registration and, separately, what
 * changes before sign-in — so a discovery-only change (e.g. removing a
 * verified domain from an all-mode provider) never reads "0 users".
 */
function ackSentence(p: RoutingPreview): string {
  const parts: string[] = [];
  const post = p.users.total - p.users.unchanged;
  if (post > 0) {
    const n = p.strandedBrokeredOnly.count;
    parts.push(`${plural(post, 'user')} will sign in differently after registration${n > 0 ? ` (${n} will be unable to sign in)` : ''}`);
  }
  if (discoveryMoving(p)) {
    const d = p.discoveryChanges!;
    const n = d.strandedBrokeredOnly.count;
    parts.push(`${plural(d.toPassword + d.toProvider + d.providerChanged, 'user')} of the new apps will be routed differently before sign-in${n > 0 ? ` (${n} could no longer sign in to the new apps)` : ''}`);
  }
  return `I understand that ${parts.join(', and ')}.`;
}

function deviceSentence(d: RoutingPreview['devices']): string {
  const parts = [
    ...(d.toOpen > 0 ? [`${d.toOpen} to fail open`] : []),
    ...(d.toClosed > 0 ? [`${d.toClosed} to fail closed`] : []),
  ];
  return `The kill switch's fail mode changes on ${d.failModeChanged} device${d.failModeChanged === 1 ? '' : 's'} (${parts.join(', ')}).`;
}

const Count = ({ label, value, icon: Icon, testId, tone = 'text-gray-900' }: {
  label: string;
  value: number;
  icon: typeof Users;
  testId: string;
  tone?: string;
}) => (
  <div className="flex items-center justify-between rounded border border-gray-200 bg-gray-50 px-3 py-2">
    <span className="inline-flex items-center gap-2 text-sm text-gray-700">
      <Icon className="w-4 h-4 text-gray-400" />
      {label}
    </span>
    <span className={`text-sm font-semibold ${value > 0 ? tone : 'text-gray-400'}`} data-testid={testId}>{value}</span>
  </div>
);

const RoutingPreviewDialog = ({
  isOpen,
  title,
  description,
  preview,
  loading = false,
  error = null,
  confirmText,
  busy = false,
  children,
  confirmDisabled = false,
  providerName = 'the identity provider',
  variant = 'primary',
  onConfirm,
  onCancel,
}: RoutingPreviewDialogProps) => {
  // The acknowledgement belongs to the preview it was given for: a new preview
  // (a re-read, or the API's 409 with fresh counts) needs a new one.
  const [ackedPreview, setAckedPreview] = useState<RoutingPreview | null>(null);
  const ack = isOpen && preview !== null && ackedPreview === preview;

  const moving = preview?.changed ?? false;
  const stranded = preview?.strandedBrokeredOnly ?? { count: 0, emails: [] };
  const needsAck = moving;
  const canConfirm = !busy && !loading && !confirmDisabled && (!needsAck || ack);
  const close = () => {
    if (!busy) onCancel();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      title={title}
      size="md"
      footer={
        <div className="flex justify-end space-x-2">
          <button type="button" className="btn-secondary" onClick={close} disabled={busy} data-testid="routing-preview-cancel">
            Cancel
          </button>
          <button
            type="button"
            className={`${variant === 'danger' ? 'btn-danger' : 'btn-primary'} disabled:opacity-50 disabled:cursor-not-allowed`}
            onClick={onConfirm}
            disabled={!canConfirm}
            data-testid="routing-preview-confirm"
          >
            {busy ? 'Saving…' : confirmText}
          </button>
        </div>
      }
    >
      <div className="space-y-4 text-sm" data-testid="routing-preview-dialog">
        <div className="text-gray-700">{description}</div>
        {children}

        {loading ? (
          <p className="text-gray-400">Working out who is affected…</p>
        ) : error ? (
          <p className="rounded border border-warning-200 bg-warning-50 px-3 py-2 text-xs text-warning-700">
            The preview could not be loaded ({error}). The API still checks the change and refuses it if users would move
            without your confirmation.
          </p>
        ) : preview ? (
          <>
            {preview.mode.before !== preview.mode.after && (
              <p className="text-xs text-gray-600" data-testid="routing-preview-mode">
                The tenant's sign-in changes from <strong>{MODE_LABEL[preview.mode.before]}</strong>
                <ArrowRight className="mx-1 inline w-3 h-3" />
                <strong>{MODE_LABEL[preview.mode.after]}</strong>.
              </p>
            )}
            {discoveryMoving(preview) && (
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">After registration</p>
            )}
            <div className="space-y-1.5">
              <Count label="Move to their SecureLink password" value={preview.users.toPassword} icon={KeyRound} testId="routing-preview-to-password" tone="text-warning-700" />
              <Count label="Move to the identity provider" value={preview.users.toProvider} icon={ArrowRight} testId="routing-preview-to-provider" tone="text-primary-700" />
              {preview.users.providerChanged > 0 && (
                <Count label="Move to another identity provider" value={preview.users.providerChanged} icon={ArrowRight} testId="routing-preview-provider-changed" tone="text-primary-700" />
              )}
              <Count label={`Unchanged (of ${preview.users.total} users)`} value={preview.users.unchanged} icon={Users} testId="routing-preview-unchanged" />
            </div>
            {preview.devices.failModeChanged > 0 && (
              <p className="flex items-start gap-1.5 text-xs text-gray-600" data-testid="routing-preview-devices">
                <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>{deviceSentence(preview.devices)}</span>
              </p>
            )}
            {stranded.count > 0 && (
              <div className="rounded border border-danger-200 bg-danger-50 px-3 py-2 text-xs text-danger-700" data-testid="routing-preview-stranded">
                <p className="flex items-start gap-1.5 font-medium">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>
                    {stranded.count} user{stranded.count === 1 ? '' : 's'} signed in only through the identity provider and
                    {stranded.count === 1 ? ' has' : ' have'} no SecureLink password. After this change they cannot sign in
                    until you give them a password, or keep the domain.
                  </span>
                </p>
                <ul className="mt-1.5 ml-5 list-disc space-y-0.5 font-mono" data-testid="routing-preview-stranded-list">
                  {stranded.emails.map((e) => (
                    <li key={e}>{e}</li>
                  ))}
                </ul>
                {stranded.count > stranded.emails.length && (
                  <p className="mt-1 ml-5">and {stranded.count - stranded.emails.length} more</p>
                )}
              </div>
            )}
            {discoveryMoving(preview) && (
              <div className="space-y-1 rounded border border-gray-200 px-3 py-2" data-testid="routing-preview-discovery">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Before sign-in (the new apps)</p>
                {preview.discoveryChanges!.toPassword > 0 && (
                  <p className="flex items-start gap-1.5 text-sm text-warning-800" data-testid="routing-preview-discovery-to-password">
                    <KeyRound className="w-4 h-4 mt-0.5 shrink-0 text-warning-600" />
                    <span>
                      {plural(preview.discoveryChanges!.toPassword, 'user')} of the new apps will be asked for a password instead of being
                      sent to {providerName}.
                    </span>
                  </p>
                )}
                {preview.discoveryChanges!.toProvider > 0 && (
                  <p className="flex items-start gap-1.5 text-sm text-gray-800" data-testid="routing-preview-discovery-to-provider">
                    <ArrowRight className="w-4 h-4 mt-0.5 shrink-0 text-gray-400" />
                    <span>
                      {plural(preview.discoveryChanges!.toProvider, 'user')} of the new apps will be sent to {providerName} instead of being
                      asked for a password.
                    </span>
                  </p>
                )}
                {preview.discoveryChanges!.providerChanged > 0 && (
                  <p className="flex items-start gap-1.5 text-sm text-gray-800" data-testid="routing-preview-discovery-provider-changed">
                    <ArrowRight className="w-4 h-4 mt-0.5 shrink-0 text-gray-400" />
                    <span>{plural(preview.discoveryChanges!.providerChanged, 'user')} of the new apps will be sent to another provider.</span>
                  </p>
                )}
                {preview.discoveryChanges!.strandedBrokeredOnly.count > 0 && (
                  <div className="text-xs text-danger-700" data-testid="routing-preview-discovery-stranded">
                    <p className="flex items-start gap-1.5 font-medium">
                      <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                      <span>
                        {plural(preview.discoveryChanges!.strandedBrokeredOnly.count, 'of them', 'of them')} sign
                        {preview.discoveryChanges!.strandedBrokeredOnly.count === 1 ? 's' : ''} in only through the identity provider (no
                        SecureLink password) and could no longer sign in to the new apps:
                      </span>
                    </p>
                    <ul className="mt-1 ml-5 list-disc space-y-0.5 font-mono">
                      {preview.discoveryChanges!.strandedBrokeredOnly.emails.map((e) => (
                        <li key={e}>{e}</li>
                      ))}
                    </ul>
                    {preview.discoveryChanges!.strandedBrokeredOnly.count > preview.discoveryChanges!.strandedBrokeredOnly.emails.length && (
                      <p className="mt-1 ml-5">
                        and {preview.discoveryChanges!.strandedBrokeredOnly.count - preview.discoveryChanges!.strandedBrokeredOnly.emails.length} more
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
            {moving ? (
              <label className="flex items-start gap-2 text-sm text-gray-800">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={ack}
                  onChange={(e) => setAckedPreview(e.target.checked ? preview : null)}
                  disabled={busy}
                  data-testid="routing-preview-ack"
                />
                <span>{ackSentence(preview)}</span>
              </label>
            ) : (
              <p className="text-xs text-gray-500" data-testid="routing-preview-no-change">
                No user changes sign-in method.
              </p>
            )}
          </>
        ) : null}
      </div>
    </Modal>
  );
};

export default RoutingPreviewDialog;
